/**
 * GithubTerraformModuleEditor - Custom Backstage Scaffolder Field Extension
 *
 * A dynamic form editor for Terraform module configurations. Fetches module
 * definitions from GitHub or Terraform registries, parses variables, and
 * generates type-appropriate input fields for editing.
 *
 * ## Overview
 *
 * This component supports two operational modes:
 *
 * 1. **Edit Mode** - Fetch and edit an existing Terraform file from GitHub
 * 2. **Template Mode** - Generate a new file from a Nunjucks template, then edit
 *
 * ## Example Templates
 *
 * See working examples in the backstage-templates repository:
 * @see https://github.com/badal-io/backstage-templates/tree/main/templates
 *
 * ---
 *
 * ### Edit Mode
 *
 * Edit Mode fetches an existing Terraform file from GitHub, parses its module
 * blocks, retrieves variable definitions from the Terraform registry, and
 * generates a form for editing. There are two ways to specify which file to edit:
 *
 * #### Option A: Dynamic File Selection (user picks via form fields)
 * @example Template: `templates/edit-tenant-module/template.yaml`
 *
 * Uses `repoParam` and `fileParam` to reference other form fields (like
 * GithubRepoPicker and GithubFilePicker) that let the user select which
 * repository and file to edit.
 *
 * ```yaml
 * # Step 1: Let user pick a repository
 * business_unit_repo:
 *   ui:field: GithubRepoPicker
 *   ui:options:
 *     owner: my-org
 *     topic: infrastructure
 *
 * # Step 2: Let user pick a file from that repository
 * config_file:
 *   ui:field: GithubFilePicker
 *   ui:options:
 *     owner: my-org
 *     repoParam: business_unit_repo    # References the repo picker above
 *     path: terraform/modules
 *     pattern: "*.tf"
 *
 * # Step 3: Edit the selected file's module variables
 * module_variables:
 *   ui:field: GithubTerraformModuleEditor
 *   ui:options:
 *     owner: my-org
 *     repoParam: business_unit_repo    # References the repo picker
 *     fileParam: config_file           # References the file picker
 *     path: terraform/modules          # Directory prefix
 *     moduleIndex: 0                   # Which module block to edit
 * ```
 *
 * #### Option B: Dynamic File Path with Suffix
 * @example Template: `templates/edit-business-unit/template.yaml`
 *
 * Uses `fileParam` to reference a form field and `fileSuffix` to construct
 * the full path. Useful when the file location includes user-selected values.
 *
 * ```yaml
 * business_unit_folder:
 *   ui:field: GithubFilePicker
 *   ui:options:
 *     owner: my-org
 *     repo: gcp-foundations-projects
 *     branch: shared
 *     pattern: "*\/shared/main.tf"
 *     valueRegex: "^([^/]+)"
 *
 * module_variables:
 *   ui:field: GithubTerraformModuleEditor
 *   ui:options:
 *     owner: my-org
 *     repo: gcp-foundations-projects
 *     branch: shared
 *     fileParam: business_unit_folder
 *     fileSuffix: shared/main.tf
 *     moduleName: business_unit
 * ```
 *
 * ---
 *
 * ### Template Mode
 * @example Template: `templates/create-tenant-new/template.yaml`
 *
 * Template Mode generates a new Terraform file from a Nunjucks template,
 * injects values from previous form steps, fetches variable definitions from
 * the registry, and displays the editor for customization before file creation.
 *
 * Use this mode when creating new infrastructure rather than editing existing files.
 *
 * ```yaml
 * # Step 1: Collect basic configuration
 * resource_name:
 *   title: Resource Name
 *   type: string
 *
 * target_repo:
 *   ui:field: GithubRepoPicker
 *   ui:options:
 *     owner: my-org
 *     topic: infrastructure
 *
 * # Step 2: Generate and edit the new module configuration
 * module_variables:
 *   ui:field: GithubTerraformModuleEditor
 *   ui:options:
 *     owner: my-org
 *     repoParam: target_repo
 *     # Template mode configuration
 *     templateMode: true
 *     templateRepo: my-templates-repo
 *     templateRepoPath: templates/create-resource
 *     templateFile: content/resource.tf
 *     moduleSource: registry.terraform.io/hashicorp/consul/aws
 *     outputFileName: resource_${{ parameters.resource_name }}.tf
 *     outputPath: terraform/resources
 *     # Values from step 1 to inject into the Nunjucks template
 *     templateValues:
 *       resource_name: ${{ parameters.resource_name }}
 *       created_by: backstage
 * ```
 *
 * ---
 *
 * ## Configuration Options
 *
 * ### Common Options (both modes)
 * | Option | Type | Default | Description |
 * |--------|------|---------|-------------|
 * | owner | string | - | GitHub organization name |
 * | moduleIndex | number | 0 | Which module block to edit if file has multiple |
 * | moduleName | string | - | Target a specific module by name instead of index |
 * | showDescriptions | boolean | true | Show variable descriptions in the form |
 * | hideSensitive | boolean | false | Hide variables marked as sensitive |
 *
 * ### Edit Mode Options
 * | Option | Type | Description |
 * |--------|------|-------------|
 * | repoParam | string | Form field name containing the repository |
 * | fileParam | string | Form field name containing the file path |
 * | path | string | Directory prefix to prepend to fileParam value |
 * | fileSuffix | string | Suffix to append to fileParam value (e.g., "shared/main.tf") |
 * | repo | string | Direct repository name (alternative to repoParam) |
 * | file | string | Direct file path (alternative to fileParam) |
 * | branch | string | Git branch to fetch from (default: repo's default branch) |
 *
 * ### Template Mode Options
 * | Option | Type | Description |
 * |--------|------|-------------|
 * | templateMode | boolean | Enable template generation mode |
 * | templateRepo | string | Repository containing the template file |
 * | templateRepoPath | string | Path to the template directory in the repo |
 * | templateFile | string | Template file path relative to templateRepoPath |
 * | moduleSource | string | Terraform module source for fetching variables |
 * | outputFileName | string | Name for the generated file |
 * | outputPath | string | Directory where the file will be created |
 * | templateValues | object | Key-value pairs to inject into the Nunjucks template |
 *
 * ---
 *
 * ## Output
 *
 * The component outputs an object containing:
 * - All variable values as key-value pairs
 * - `_changesSummary`: Markdown summary of changes (for PR descriptions)
 * - `_onelineSummary`: Short summary for PR titles
 * - `_moduleSource`, `_moduleVersion`, `_moduleName`: Module metadata
 * - In template mode: `_renderedContent` with the full generated file
 */

import { useEffect, useState, useCallback, useRef, useMemo } from 'react';
import {
  Box,
  Typography,
  CircularProgress,
  Paper,
  Divider,
  Chip,
  TextField,
  Button,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  makeStyles,
  Theme,
  createStyles,
} from '@material-ui/core';
import WarningIcon from '@material-ui/icons/Warning';
import VisibilityIcon from '@material-ui/icons/Visibility';
import VisibilityOffIcon from '@material-ui/icons/VisibilityOff';
import HelpOutlineIcon from '@material-ui/icons/HelpOutline';
import GitHubIcon from '@material-ui/icons/GitHub';
import DescriptionIcon from '@material-ui/icons/Description';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import { Alert, ToggleButton, ToggleButtonGroup } from '@material-ui/lab';
import Accordion from '@material-ui/core/Accordion';
import AccordionSummary from '@material-ui/core/AccordionSummary';
import AccordionDetails from '@material-ui/core/AccordionDetails';
import { FieldExtensionComponentProps } from '@backstage/plugin-scaffolder-react';
import { useApi, githubAuthApiRef, configApiRef, fetchApiRef } from '@backstage/core-plugin-api';
import { catalogApiRef } from '@backstage/plugin-catalog-react';
import {
  TerraformVariablesEditorOptions,
  TerraformVariableSchema,
  isHiddenValue,
} from './TerraformVariablesEditor/types';
import { VariableEditor } from './TerraformVariablesEditor/VariableEditor';
import { EditorProvider } from './TerraformVariablesEditor/EditorContext';
// Frontend HCL parsing services (eliminates WASM crash risk on backend)
import {
  parseHcl,
  extractModuleBlocks,
  isWasmAvailable,
} from './services/hclParser';
import {
  parseModuleSource,
} from './services/moduleSourceParser';
import nunjucks from 'nunjucks';
import {
  fetchModuleVariables as fetchVariablesFromSource,
  fetchModuleVersions,
  type FetchVariablesResult,
  type ModuleVersionInfo,
} from './services/registryClient';

// Verify imports loaded successfully
console.log('[GithubTerraformModuleEditor] Module loaded, imports:', {
  parseHcl: typeof parseHcl,
  extractModuleBlocks: typeof extractModuleBlocks,
  isWasmAvailable: typeof isWasmAvailable,
  parseModuleSource: typeof parseModuleSource,
  fetchVariablesFromSource: typeof fetchVariablesFromSource,
});

export interface GithubTerraformModuleEditorOptions extends TerraformVariablesEditorOptions {
  /** GitHub organization name */
  owner?: string;
  /** Form field name containing the selected repository (for dynamic selection) */
  repoParam?: string;
  /** Form field name containing the selected file path (for dynamic selection) */
  fileParam?: string;
  /** Directory prefix to prepend to the file path */
  path?: string;
  /** Direct repository name (alternative to repoParam for known repos) */
  repo?: string;
  /** Direct file path (alternative to fileParam for known paths) */
  file?: string;
  /** Git branch to fetch from (default: repo's default branch) */
  branch?: string;
  /** Suffix to append to fileParam value (e.g., "/shared/main.tf") */
  fileSuffix?: string;
  /** Which module block to edit if the file contains multiple (default: 0) */
  moduleIndex?: number;
  /** Target a specific module by name instead of using moduleIndex */
  moduleName?: string;
  /** Enable template mode to generate a new file from a Nunjucks template */
  templateMode?: boolean;
  /** Terraform module source for fetching variables (e.g., "registry.terraform.io/hashicorp/consul/aws") */
  moduleSource?: string;
  /** Template file path relative to templateRepoPath (e.g., "content/module.tf") */
  templateFile?: string;
  /** Key-value pairs to inject into the Nunjucks template */
  templateValues?: Record<string, unknown>;
  /** Name for the generated output file (e.g., "my_module.tf") */
  outputFileName?: string;
  /** Directory where the generated file will be placed (e.g., "terraform/modules") */
  outputPath?: string;
  /** GitHub repository containing the template file */
  templateRepo?: string;
  /** Path to the template directory within templateRepo */
  templateRepoPath?: string;
}

interface ParsedModuleResponse {
  modules: Array<{
    name: string;
    source: string;
    version?: string;
    currentValues: Record<string, unknown>;
  }>;
  variables: TerraformVariableSchema[];
  warnings: string[];
  moduleSource: string;
  moduleVersion?: string;
}

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      width: '100%',
    },
    header: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      marginBottom: theme.spacing(2),
    },
    loadingContainer: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: theme.spacing(4),
    },
    variableCard: {
      padding: theme.spacing(2),
      marginBottom: theme.spacing(2),
      '&:last-child': {
        marginBottom: 0,
      },
    },
    section: {
      marginBottom: theme.spacing(3),
    },
    sectionTitle: {
      display: 'flex',
      alignItems: 'center',
      gap: theme.spacing(1),
      marginBottom: theme.spacing(2),
    },
    warningAlert: {
      marginBottom: theme.spacing(2),
    },
    moduleInfo: {
      display: 'flex',
      gap: theme.spacing(2),
      marginBottom: theme.spacing(2),
      flexWrap: 'wrap',
    },
    moduleChip: {
      fontSize: '0.75rem',
    },
    badge: {
      fontSize: '0.7rem',
    },
    modeToggle: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'flex-end',
      marginBottom: theme.spacing(2),
      padding: theme.spacing(1),
      backgroundColor:
        theme.palette.type === 'dark'
          ? 'rgba(255, 255, 255, 0.03)'
          : 'rgba(0, 0, 0, 0.02)',
      borderRadius: theme.shape.borderRadius,
    },
    textEditor: {
      width: '100%',
      minHeight: 300,
      fontFamily: 'monospace',
      fontSize: 12,
      padding: theme.spacing(1.5),
      border: `1px solid ${theme.palette.divider}`,
      borderRadius: theme.shape.borderRadius,
      backgroundColor:
        theme.palette.type === 'dark'
          ? 'rgba(0, 0, 0, 0.2)'
          : 'rgba(255, 255, 255, 0.9)',
      color: theme.palette.text.primary,
      resize: 'vertical',
      '&:focus': {
        outline: 'none',
        borderColor: theme.palette.primary.main,
      },
    },
    textEditorError: {
      borderColor: theme.palette.error.main,
    },
    showHiddenButton: {
      marginTop: theme.spacing(1),
      marginBottom: theme.spacing(2),
    },
    variableHeader: {
      display: 'flex',
      alignItems: 'flex-start',
      gap: theme.spacing(0.5),
    },
    variableContent: {
      flex: 1,
      minWidth: 0,
    },
    helpIcon: {
      padding: 2,
      marginTop: 2,
      color: theme.palette.text.secondary,
      '&:hover': {
        color: theme.palette.primary.main,
      },
    },
    typeDefinitionCode: {
      fontFamily: 'monospace',
      fontSize: 12,
      whiteSpace: 'pre-wrap',
      wordBreak: 'break-word',
      backgroundColor:
        theme.palette.type === 'dark'
          ? 'rgba(0, 0, 0, 0.2)'
          : 'rgba(0, 0, 0, 0.04)',
      padding: theme.spacing(2),
      borderRadius: theme.shape.borderRadius,
      maxHeight: 400,
      overflow: 'auto',
    },
    versionSelector: {
      display: 'flex',
      alignItems: 'center',
      gap: theme.spacing(2),
      marginBottom: theme.spacing(2),
      padding: theme.spacing(2),
      backgroundColor:
        theme.palette.type === 'dark'
          ? 'rgba(255, 255, 255, 0.03)'
          : 'rgba(0, 0, 0, 0.02)',
      borderRadius: theme.shape.borderRadius,
    },
    versionSelect: {
      minWidth: 200,
    },
    incompatibleCard: {
      borderColor: theme.palette.error.main,
      borderWidth: 2,
      backgroundColor:
        theme.palette.type === 'dark'
          ? 'rgba(211, 47, 47, 0.1)'
          : 'rgba(211, 47, 47, 0.05)',
    },
    incompatibleWarning: {
      display: 'flex',
      alignItems: 'center',
      gap: theme.spacing(1),
      padding: theme.spacing(1),
      marginBottom: theme.spacing(1),
      backgroundColor:
        theme.palette.type === 'dark'
          ? 'rgba(211, 47, 47, 0.2)'
          : 'rgba(211, 47, 47, 0.1)',
      borderRadius: theme.shape.borderRadius,
      color: theme.palette.error.main,
    },
    latestChip: {
      marginLeft: theme.spacing(1),
      backgroundColor: theme.palette.success.main,
      color: theme.palette.success.contrastText,
    },
    moduleLinks: {
      display: 'flex',
      alignItems: 'center',
      gap: theme.spacing(0.5),
      marginLeft: theme.spacing(1),
    },
    moduleLink: {
      padding: theme.spacing(0.5),
      color: theme.palette.text.secondary,
      '&:hover': {
        color: theme.palette.primary.main,
      },
    },
    terraformIcon: {
      width: 20,
      height: 20,
    },
  }),
);

type FormDataType = Record<string, unknown>;

export const GithubTerraformModuleEditor = (
  props: FieldExtensionComponentProps<FormDataType, GithubTerraformModuleEditorOptions>,
) => {
  console.log('[GithubTerraformModuleEditor] Component rendering', { props });

  const {
    onChange,
    rawErrors,
    required,
    formData = {},
    formContext,
    uiSchema,
    schema,
  } = props;

  const classes = useStyles();
  const githubAuth = useApi(githubAuthApiRef);
  const configApi = useApi(configApiRef);
  const fetchApi = useApi(fetchApiRef);
  const catalogApi = useApi(catalogApiRef);

  // Safely get backend URL with fallback
  let backendUrl: string;
  try {
    backendUrl = configApi.getString('backend.baseUrl');
  } catch {
    // Fallback to relative URL if config not available
    backendUrl = '';
  }

  const options = uiSchema?.['ui:options'] || {};
  const {
    owner = 'badal-io',
    repoParam,
    fileParam,
    path = '',
    // Static repo/file options (alternative to repoParam/fileParam for known paths)
    repo: staticRepo,
    file: staticFile,
    // Git branch to fetch from (default: repo's default branch)
    branch,
    // Suffix to append to fileParam value (e.g., "/shared/main.tf")
    fileSuffix = '',
    moduleIndex = 0,
    hideSensitive = false,
    showDescriptions = true,
    templateMode = false,
    moduleSource: configModuleSource,
    moduleName: targetModuleName,
    // Template mode options for file generation
    templateFile,
    templateValues,
    outputFileName,
    outputPath,
    templateRepo = 'backstage-templates',
    templateRepoPath,
  } = options;

  // Get values from other form fields
  const formValues = formContext?.formData as Record<string, unknown> | undefined;
  const rawRepoValue = repoParam ? (formValues?.[repoParam] as string) : staticRepo;
  const rawFileValue = fileParam ? (formValues?.[fileParam] as string) : staticFile;

  console.log('[GithubTerraformModuleEditor] Form values:', {
    repoParam,
    fileParam,
    staticRepo,
    staticFile,
    branch,
    fileSuffix,
    rawRepoValue,
    rawFileValue,
    formValuesKeys: formValues ? Object.keys(formValues) : [],
  });

  // Extract repo name from entity ref format if needed
  const repoName = rawRepoValue?.includes('/')
    ? rawRepoValue.split('/').pop()
    : rawRepoValue;

  // Build full file path:
  // 1. If path is set, prepend it: path/rawFileValue
  // 2. If fileSuffix is set, append it: rawFileValue/suffix
  // 3. Both can be combined: path/rawFileValue/suffix
  let filePath = rawFileValue;
  if (path && rawFileValue) {
    filePath = `${path}/${rawFileValue}`;
  }
  if (fileSuffix && filePath) {
    // Ensure no double slashes
    const suffix = fileSuffix.startsWith('/') ? fileSuffix.slice(1) : fileSuffix;
    filePath = `${filePath}/${suffix}`;
  }

  console.log('[GithubTerraformModuleEditor] Derived values:', {
    repoName,
    filePath,
    path,
  });

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [variables, setVariables] = useState<TerraformVariableSchema[]>([]);
  const [warnings, setWarnings] = useState<string[]>([]);
  const [moduleInfo, setModuleInfo] = useState<{
    source?: string;
    version?: string;
    name?: string;
  }>({});

  // Version selection state
  const [availableVersions, setAvailableVersions] = useState<ModuleVersionInfo[]>([]);
  const [selectedVersion, setSelectedVersion] = useState<string | 'latest' | null>(null);
  const [latestVersion, setLatestVersion] = useState<string | null>(null);
  const [versionsLoading, setVersionsLoading] = useState(false);
  const [incompatibleVariables, setIncompatibleVariables] = useState<Set<string>>(new Set());
  // Store the parsed source info for re-fetching on version change
  const [sourceInfo, setSourceInfo] = useState<ReturnType<typeof parseModuleSource> | null>(null);
  // Backstage catalog entity link (if the module source repo exists in catalog)
  const [backstageEntityLink, setBackstageEntityLink] = useState<string | null>(null);
  // Template mode state
  const [rawTemplateContent, setRawTemplateContent] = useState<string | null>(null);
  const [renderedTemplateContent, setRenderedTemplateContent] = useState<string | null>(null);
  const [templateError, setTemplateError] = useState<string | null>(null);

  // Local state for variable values to handle the case where formData is a string (HCL)
  // or when we need to optimistically update before async parsing.
  // This serves as the source of truth for the UI when in templateMode (where output is string).
  const [localValues, setLocalValues] = useState<Record<string, unknown>>({});
  const lastRenderedStringRef = useRef<string | null>(null);

  // Sync localValues from formData when it changes externally (e.g. navigation)
  useEffect(() => {
    const syncFromFormData = async () => {
      // If formData is object, it might be legacy state or normal mode.
      // We merge it into localValues.
      if (formData && typeof formData === 'object') {
        setLocalValues(prev => {
          // Simple shallow merge to avoid unnecessary updates if possible,
          // but for now just returning merged object.
          // We filter out _renderedContent to avoid polluting local values if it exists.
          const cleanFormData = { ...formData };
          delete cleanFormData._renderedContent;
          return { ...prev, ...cleanFormData };
        });
        return;
      }

      // If formData is string (templateMode output), and differs from what we just rendered
      // (meaning it comes from an external source like Back/Next navigation), parse it.
      if (typeof formData === 'string' && formData !== lastRenderedStringRef.current) {
        try {
          console.log('[GithubTerraformModuleEditor] Parsing formData string to restore state...');
          // Use a dummy filename, content is what matters
          const parsed = await parseHcl('restored.tf', formData);
          const blocks = extractModuleBlocks(parsed);
          if (blocks.length > 0) {
            // We assume the first module block contains our variables
            console.log('[GithubTerraformModuleEditor] Restored values:', blocks[0].currentValues);
            setLocalValues(blocks[0].currentValues);
          }
        } catch (e) {
          console.error('[GithubTerraformModuleEditor] Failed to parse formData string:', e);
        }
      }
    };
    syncFromFormData();
  }, [formData]);

  // Resolve the output filename by replacing template expressions with actual values
  // Use formValues (actual form data from step 1) instead of templateValues (which contains unresolved expressions)
  const resolvedOutputFileName = useMemo(() => {
    if (!outputFileName) return 'Generated Terraform File';

    // Replace ${{ parameters.name }} or ${{ values.name }} with the actual value from formValues
    return outputFileName.replace(/\$\{\{\s*(parameters|values)\.(\w+)\s*\}\}/g, (match, _namespace, key) => {
      const value = formValues?.[key];
      if (value === undefined || value === null) return match;
      return String(value);
    });
  }, [outputFileName, formValues]);

  // Build Terraform registry URL based on source info and selected version
  const getTerraformRegistryUrl = useCallback((info: ReturnType<typeof parseModuleSource> | null, version: string | 'latest' | null): string | null => {
    if (!info || info.type !== 'registry') return null;

    const effectiveVersion = version === 'latest' ? latestVersion : version;

    if (info.registryType === 'private') {
      // Private TFC registry: https://app.terraform.io/app/{org}/registry/modules/private/{namespace}/{name}/{provider}/{version}
      const org = info.organization || info.namespace;
      if (!org || !info.namespace || !info.name || !info.provider) return null;
      const versionPath = effectiveVersion ? `/${effectiveVersion}` : '';
      return `https://app.terraform.io/app/${org}/registry/modules/private/${info.namespace}/${info.name}/${info.provider}${versionPath}`;
    } else {
      // Public registry: https://registry.terraform.io/modules/{namespace}/{name}/{provider}/{version}
      if (!info.namespace || !info.name || !info.provider) return null;
      const versionPath = effectiveVersion ? `/${effectiveVersion}` : '';
      return `https://registry.terraform.io/modules/${info.namespace}/${info.name}/${info.provider}${versionPath}`;
    }
  }, [latestVersion]);

  // Build GitHub URL based on source info
  const getGitHubUrl = useCallback((info: ReturnType<typeof parseModuleSource> | null): string | null => {
    if (!info) return null;

    if (info.type === 'github' && info.owner && info.repo) {
      // Direct GitHub source
      let url = `https://github.com/${info.owner}/${info.repo}`;
      if (info.ref) {
        url += `/tree/${info.ref}`;
      }
      if (info.subdir) {
        url += `/${info.subdir}`;
      }
      return url;
    }

    // For registry modules, we can try to derive GitHub from VCS info
    // Most Terraform registry modules link to their GitHub source
    // For now, return null if not a direct GitHub source
    return null;
  }, []);

  // Check if the module source repository exists in the Backstage catalog
  useEffect(() => {
    const checkCatalog = async () => {
      if (!sourceInfo) {
        setBackstageEntityLink(null);
        return;
      }

      let repoOwner: string | undefined;
      let repoName: string | undefined;

      if (sourceInfo.type === 'github' && sourceInfo.owner && sourceInfo.repo) {
        repoOwner = sourceInfo.owner;
        repoName = sourceInfo.repo;
      } else if (sourceInfo.type === 'registry' && sourceInfo.namespace && sourceInfo.name) {
        // For registry modules, try to find by module name
        // This is a heuristic - the actual repo name might differ
        repoOwner = sourceInfo.namespace;
        repoName = sourceInfo.name;
      }

      if (!repoOwner || !repoName) {
        setBackstageEntityLink(null);
        return;
      }

      try {
        // Search for entities with matching annotations
        const entities = await catalogApi.getEntities({
          filter: {
            'metadata.annotations.github.com/project-slug': `${repoOwner}/${repoName}`,
          },
        });

        if (entities.items.length > 0) {
          const entity = entities.items[0];
          const namespace = entity.metadata.namespace || 'default';
          const kind = entity.kind.toLowerCase();
          const name = entity.metadata.name;
          setBackstageEntityLink(`/catalog/${namespace}/${kind}/${name}`);
          return;
        }

        // Also try matching by name for repos that might not have the annotation
        const entitiesByName = await catalogApi.getEntities({
          filter: {
            'metadata.name': repoName,
            kind: 'Component',
          },
        });

        if (entitiesByName.items.length > 0) {
          const entity = entitiesByName.items[0];
          const namespace = entity.metadata.namespace || 'default';
          const kind = entity.kind.toLowerCase();
          const name = entity.metadata.name;
          setBackstageEntityLink(`/catalog/${namespace}/${kind}/${name}`);
          return;
        }

        setBackstageEntityLink(null);
      } catch (err) {
        console.error('[GithubTerraformModuleEditor] Error checking catalog:', err);
        setBackstageEntityLink(null);
      }
    };

    checkCatalog();
  }, [sourceInfo, catalogApi]);

  // Editor mode: 'gui' (structured), 'text' (per-variable), 'raw' (raw JSON)
  type EditorMode = 'gui' | 'text' | 'raw';
  const [editorMode, setEditorMode] = useState<EditorMode>('gui');
  const [rawEditorValue, setRawEditorValue] = useState('');
  const [rawEditorError, setRawEditorError] = useState<string | null>(null);
  const [showHiddenVariables, setShowHiddenVariables] = useState(false);
  // Track variables that have been edited - keep them visible even when "show hidden" is off
  const [editedVariables, setEditedVariables] = useState<Set<string>>(new Set());
  // Dialog for showing full type definition
  const [typeDefinitionDialog, setTypeDefinitionDialog] = useState<{
    open: boolean;
    variableName: string;
    rawType: string;
  }>({ open: false, variableName: '', rawType: '' });

  // Track original values from Terraform file for generating change summary
  const originalValuesRef = useRef<Record<string, unknown> | null>(null);

  // Helper function to recursively parse JSON strings into proper objects/arrays
  // This ensures data is properly typed before being passed to the scaffolder
  // Handles the case where wizard step navigation serializes objects to JSON strings
  const parseJsonStrings = useCallback((value: unknown): unknown => {
    if (typeof value === 'string') {
      const trimmed = value.trim();
      // Handle the literal string "null" - convert to actual null
      if (trimmed === 'null') {
        return null;
      }
      // Try to parse JSON objects and arrays
      if ((trimmed.startsWith('{') && trimmed.endsWith('}')) ||
          (trimmed.startsWith('[') && trimmed.endsWith(']'))) {
        try {
          const parsed = JSON.parse(trimmed);
          // Recursively parse nested values
          if (parsed !== null && typeof parsed === 'object') {
            if (Array.isArray(parsed)) {
              return parsed.map(item => parseJsonStrings(item));
            }
            const result: Record<string, unknown> = {};
            for (const [k, v] of Object.entries(parsed as Record<string, unknown>)) {
              result[k] = parseJsonStrings(v);
            }
            return result;
          }
          return parsed;
        } catch {
          // Not valid JSON, return as-is
        }
      }
    }
    // Recursively process existing objects
    if (value !== null && typeof value === 'object' && !Array.isArray(value)) {
      const result: Record<string, unknown> = {};
      for (const [k, v] of Object.entries(value as Record<string, unknown>)) {
        result[k] = parseJsonStrings(v);
      }
      return result;
    }
    // Recursively process arrays
    if (Array.isArray(value)) {
      return value.map(item => parseJsonStrings(item));
    }
    return value;
  }, []);

  // Helper function to render a template with Nunjucks using Backstage-style syntax
  const renderTemplate = useCallback((template: string, values: Record<string, unknown>): string => {
    // Configure Nunjucks environment
    // Disable whitespace stripping to prevent accidental line merges in HCL
    const env = new nunjucks.Environment(null, {
      autoescape: false,
      throwOnUndefined: false,
      trimBlocks: false,
      lstripBlocks: false,
    });

    // Convert Backstage syntax (${{ expr }}) to Nunjucks syntax ({{ expr }})
    const nunjucksTemplate = template.replace(/\$\{\{/g, '{{').replace(/\}\}/g, '}}');

    // Create render context with 'values' namespace (as used in Backstage templates)
    const renderContext = { values };

    return env.renderString(nunjucksTemplate, renderContext);
  }, []);

  // Effect to re-render template when localValues changes (in templateMode)
  useEffect(() => {
    if (!templateMode || !rawTemplateContent) return;

    try {
      // Merge module variable data with formValues (form values take precedence for template parameters)
      // localValues contains module variable edits
      // formValues contains actual form values (e.g., environments: [...array...])
      // formValues must come LAST to override module variable defaults with context values
      // BUT for variables that are NOT in formValues (user edited module vars), we use localValues.
      
      const mergedValues = {
        ...localValues,
        ...formValues,
      };

      console.log('[GithubTerraformModuleEditor] Re-rendering template with values:', Object.keys(mergedValues));

      const rendered = renderTemplate(rawTemplateContent, mergedValues);
      setRenderedTemplateContent(rendered);
      setTemplateError(null);
      lastRenderedStringRef.current = rendered;

      // Update localValues with rendered content if it changed
      let currentValues = localValues;
      if (localValues._renderedContent !== rendered) {
          currentValues = {
              ...localValues,
              _renderedContent: rendered
          };
          setLocalValues(currentValues);
      }

      // Sync to formData (upstream) if anything changed
      // We must return an object to satisfy the schema (type: object)
      // We use a JSON stringify comparison to avoid deep equality complexity
      // and prevent infinite loops if the object identity changes but content doesn't.
      if (JSON.stringify(formData) !== JSON.stringify(currentValues)) {
          onChange(currentValues);
      }
    } catch (err) {
      console.error('[GithubTerraformModuleEditor] Template rendering error:', err);
      setTemplateError(`Template rendering error: ${err instanceof Error ? err.message : String(err)}`);
    }
  }, [templateMode, rawTemplateContent, localValues, formValues, renderTemplate, onChange, formData]);

  // Sync raw editor value when switching to raw mode or when localValues changes
  useEffect(() => {
    if (editorMode === 'raw') {
      // Filter out internal keys (starting with _) for display
      const displayValues = Object.fromEntries(
        Object.entries(localValues).filter(([key]) => !key.startsWith('_'))
      );
      setRawEditorValue(JSON.stringify(displayValues, null, 2));
      setRawEditorError(null);
    }
  }, [editorMode, localValues]);

  // Handle raw editor changes
  const handleRawEditorChange = useCallback(
    (e: React.ChangeEvent<HTMLTextAreaElement>) => {
      const newValue = e.target.value;
      setRawEditorValue(newValue);

      try {
        const parsed = JSON.parse(newValue);
        setRawEditorError(null);
        
        // Merge preserved internal keys back into the new values
        const internalKeys = Object.fromEntries(
          Object.entries(localValues).filter(([key]) => key.startsWith('_'))
        );
        const newLocalValues = { ...internalKeys, ...parsed };
        
        setLocalValues(newLocalValues);
        if (!templateMode) {
             onChange(newLocalValues);
        }
      } catch {
        setRawEditorError('Invalid JSON');
      }
    },
    [onChange, templateMode, localValues],
  );

  // Handle mode change
  const handleModeChange = useCallback(
    (_event: React.MouseEvent<HTMLElement>, newMode: EditorMode | null) => {
      if (newMode === null) return; // Don't allow deselecting all

      if (newMode === 'raw') {
        // Switching to raw mode - sync current localValues to raw editor (filtered)
        const displayValues = Object.fromEntries(
          Object.entries(localValues).filter(([key]) => !key.startsWith('_'))
        );
        setRawEditorValue(JSON.stringify(displayValues, null, 2));
        setRawEditorError(null);
      }
      setEditorMode(newMode);
    },
    [localValues],
  );

  // Helper to extract error message from various error formats
  const extractErrorMessage = (err: unknown): string => {
    if (typeof err === 'string') return err;
    if (err instanceof Error) return err.message;
    if (err && typeof err === 'object') {
      const errObj = err as Record<string, unknown>;
      if (typeof errObj.message === 'string') return errObj.message;
      if (typeof errObj.error === 'string') return errObj.error;
      if (errObj.error && typeof errObj.error === 'object') {
        const nested = errObj.error as Record<string, unknown>;
        if (typeof nested.message === 'string') return nested.message;
      }
      if (typeof errObj.details === 'string') return errObj.details;
    }
    return 'Unknown error occurred';
  };

  /**
   * Generate a human-readable summary of changes between original and current values
   * Format:
   * - changed field "x" to "VALUE"
   * - added "NEWKEY" to map "MAPNAME" with value { json }
   * - deleted "KEY" from map "MAPNAME"
   */
  const generateChangesSummary = useCallback((
    original: Record<string, unknown>,
    current: Record<string, unknown>,
    variableSchemas: TerraformVariableSchema[],
  ): string => {
    const changes: string[] = [];

    // Helper to format a value for display
    const formatValue = (val: unknown): string => {
      if (val === null) return 'null';
      if (val === undefined) return 'undefined';
      if (typeof val === 'string') return `"${val}"`;
      if (typeof val === 'object') return JSON.stringify(val);
      return String(val);
    };

    // Helper to compare two values deeply
    const valuesEqual = (a: unknown, b: unknown): boolean => {
      if (a === b) return true;
      if (a === null || b === null) return a === b;
      if (typeof a !== typeof b) return false;
      if (typeof a === 'object') {
        return JSON.stringify(a) === JSON.stringify(b);
      }
      return false;
    };

    // Helper to compare maps and generate specific changes
    const compareMapValues = (
      fieldName: string,
      origMap: Record<string, unknown> | undefined,
      currMap: Record<string, unknown> | undefined,
    ) => {
      const orig = origMap || {};
      const curr = currMap || {};
      const origKeys = new Set(Object.keys(orig));
      const currKeys = new Set(Object.keys(curr));

      // Find added keys
      for (const key of currKeys) {
        if (!origKeys.has(key)) {
          changes.push(`added "${key}" to map "${fieldName}" with value ${formatValue(curr[key])}`);
        }
      }

      // Find deleted keys
      for (const key of origKeys) {
        if (!currKeys.has(key)) {
          changes.push(`deleted "${key}" from map "${fieldName}"`);
        }
      }

      // Find changed keys
      for (const key of currKeys) {
        if (origKeys.has(key) && !valuesEqual(orig[key], curr[key])) {
          changes.push(`changed "${fieldName}.${key}" to ${formatValue(curr[key])}`);
        }
      }
    };

    // Compare each variable
    for (const variable of variableSchemas) {
      const fieldName = variable.name;
      const origVal = original[fieldName];
      const currVal = current[fieldName];

      // Skip if values are equal
      if (valuesEqual(origVal, currVal)) continue;

      // Handle map types specially
      if (variable.type === 'map' || (
        typeof origVal === 'object' && origVal !== null && !Array.isArray(origVal) &&
        typeof currVal === 'object' && currVal !== null && !Array.isArray(currVal)
      )) {
        compareMapValues(
          fieldName,
          origVal as Record<string, unknown> | undefined,
          currVal as Record<string, unknown> | undefined,
        );
      } else {
        // Simple field change
        changes.push(`changed field "${fieldName}" to ${formatValue(currVal)}`);
      }
    }

    return changes.join('\n');
  }, []);

  // Generate changes summary whenever localValues changes
  const changesSummary = useMemo(() => {
    if (!originalValuesRef.current || variables.length === 0) {
      return '';
    }
    return generateChangesSummary(originalValuesRef.current, localValues, variables);
  }, [localValues, variables, generateChangesSummary]);

  // Generate one-line summary for PR title
  const onelineSummary = useMemo(() => {
    if (!filePath) return '';
    return `Updated ${filePath}`;
  }, [filePath]);

  // Update the form data with changes summary whenever it changes
  // Use a separate effect to avoid infinite loops
  const lastSummaryRef = useRef<string>('');
  const lastOnelineRef = useRef<string>('');
  useEffect(() => {
    // Only update if summary actually changed and we have original values
    const summaryChanged = changesSummary !== lastSummaryRef.current && originalValuesRef.current;
    const onelineChanged = onelineSummary !== lastOnelineRef.current;

    if (summaryChanged || onelineChanged) {
      lastSummaryRef.current = changesSummary;
      lastOnelineRef.current = onelineSummary;
      // Include the changes summary and one-line summary as special fields
      const currentSummary = localValues._changesSummary;
      const currentOneline = localValues._onelineSummary;
      if (currentSummary !== changesSummary || currentOneline !== onelineSummary) {
        // Update localValues with summary fields
        const newLocalValues = {
             ...localValues,
             _changesSummary: changesSummary,
             _onelineSummary: onelineSummary,
        };
        setLocalValues(newLocalValues);

        if (!templateMode) {
             onChange({
                  ...formData,
                  ...newLocalValues
             });
        }
      }
    }
  }, [changesSummary, onelineSummary, localValues, templateMode, formData, onChange]);

  // Fetch and parse the module from GitHub (or directly from module source in template mode)
  // Uses frontend WASM parsing to eliminate backend crash risks
  const fetchModuleVariables = useCallback(async () => {
    console.log('[GithubTerraformModuleEditor] fetchModuleVariables called', {
      repoName,
      filePath,
      owner,
      moduleIndex,
      templateMode,
      configModuleSource,
    });

    // Template mode: fetch variables directly from module source without needing a file
    if (templateMode) {
      if (!configModuleSource) {
        console.log('[GithubTerraformModuleEditor] Template mode but no moduleSource configured');
        setError('Template mode requires moduleSource to be configured');
        return;
      }

      setLoading(true);
      setError(null);

      try {
        console.log('[GithubTerraformModuleEditor] Template mode: parsing module source...');
        const parsedSourceInfo = parseModuleSource(configModuleSource);
        console.log('[GithubTerraformModuleEditor] Parsed source info:', parsedSourceInfo);

        // Store source info for version changes
        setSourceInfo(parsedSourceInfo);

        // Get GitHub token for fetching (might be needed for private registries)
        const token = await githubAuth.getAccessToken(['repo']);

        // Fetch available versions
        console.log('[GithubTerraformModuleEditor] Template mode: Fetching available versions...');
        setVersionsLoading(true);
        fetchModuleVersions(parsedSourceInfo, {
          githubToken: token,
          fetchApi,
          backendUrl,
        }).then(versionsResult => {
          console.log('[GithubTerraformModuleEditor] Versions fetched:', versionsResult);
          setAvailableVersions(versionsResult.versions);
          setLatestVersion(versionsResult.latestVersion || null);
          // In template mode, default to latest version
          setSelectedVersion('latest');
          setVersionsLoading(false);
        }).catch(err => {
          console.error('[GithubTerraformModuleEditor] Failed to fetch versions:', err);
          setVersionsLoading(false);
        });

        // Fetch variables from the module source
        console.log('[GithubTerraformModuleEditor] Template mode: Fetching variables...');
        const variablesResult: FetchVariablesResult = await fetchVariablesFromSource(
          parsedSourceInfo,
          {
            githubToken: token,
            fetchApi,
            backendUrl,
            version: undefined, // Use latest
            currentValues: {}, // No current values in template mode
          },
        );

        console.log('[GithubTerraformModuleEditor] Variables fetched:', {
          count: variablesResult.variables.length,
          source: variablesResult.source,
          warnings: variablesResult.warnings,
        });

        setVariables(variablesResult.variables);
        setWarnings(variablesResult.warnings || []);

        // Set module info
        const templateModuleInfo = {
          source: configModuleSource,
          version: undefined, // Latest
          name: 'new-module', // Placeholder name
        };
        setModuleInfo(templateModuleInfo);

        // Initialize form data with default values from module variables
        const initialData: Record<string, unknown> = {};
        for (const variable of variablesResult.variables) {
          if (variable.default !== undefined) {
            initialData[variable.name] = parseJsonStrings(variable.default);
          }
        }

        // Fetch and render the template file if provided
        if (templateFile && templateRepo) {
          console.log('[GithubTerraformModuleEditor] Fetching template file from GitHub...');

          // Build the full path to the template file
          const templateFullPath = templateRepoPath
            ? `${templateRepoPath}/${templateFile}`
            : templateFile;

          const templateGithubUrl = `https://api.github.com/repos/${owner}/${templateRepo}/contents/${templateFullPath}`;
          console.log('[GithubTerraformModuleEditor] Template URL:', templateGithubUrl);

          try {
            const templateResponse = await fetch(templateGithubUrl, {
              headers: {
                Authorization: `Bearer ${token}`,
                Accept: 'application/vnd.github.v3+json',
              },
            });

            if (!templateResponse.ok) {
              if (templateResponse.status === 404) {
                throw new Error(`Template file not found: ${templateFullPath}`);
              }
              if (templateResponse.status === 401 || templateResponse.status === 403) {
                throw new Error(`Access denied to template repository '${templateRepo}'. Please ensure you have access to this repository.`);
              }
              throw new Error(`Failed to fetch template (${templateResponse.status}): ${templateResponse.statusText}`);
            }

            const templateData = await templateResponse.json();
            const rawTemplate = atob(templateData.content);
            console.log('[GithubTerraformModuleEditor] Template content length:', rawTemplate.length);

            // Store the raw template content
            setRawTemplateContent(rawTemplate);

            // Merge initial module variable data with formValues (form data takes precedence)
            // formValues must come LAST to override module defaults with actual form data
            const mergedValues = {
              ...initialData, // Module variable defaults (e.g., environments: "")
              ...formValues, // Actual form values override defaults (e.g., environments: [...])
            };

            // Render the template with merged values
            try {
              const rendered = renderTemplate(rawTemplate, mergedValues);
              setRenderedTemplateContent(rendered);
              // initialData._renderedContent = rendered; // Do not store rendered content in local values
              console.log('[GithubTerraformModuleEditor] Template rendered successfully');

              // Parse the rendered HCL to extract actual module values
              // This allows the GUI/Text/Raw editors to show the real values from the template
              let parsedValues: Record<string, unknown> = {};
              
              try {
                const parsedRendered = await parseHcl('rendered.tf', rendered);
                const renderedModuleBlocks = extractModuleBlocks(parsedRendered);
                console.log('[GithubTerraformModuleEditor] Parsed rendered template:', {
                  moduleCount: renderedModuleBlocks.length,
                  moduleNames: renderedModuleBlocks.map(m => m.name),
                });

                if (renderedModuleBlocks.length > 0) {
                  const targetModule = renderedModuleBlocks[0];
                  parsedValues = targetModule.currentValues;
                  
                  // Update module info with parsed module name
                  setModuleInfo({
                    source: targetModule.source || configModuleSource,
                    version: targetModule.version,
                    name: targetModule.name,
                  });
                  // Store original values for change tracking
                  if (!originalValuesRef.current) {
                    originalValuesRef.current = { ...targetModule.currentValues };
                  }
                }
              } catch (parseErr) {
                console.warn('[GithubTerraformModuleEditor] Could not parse rendered template:', parseErr);
                // Continue with fallback parsing
              }

              // FALLBACK: Scan the rendered string with regex to extract assignments for any missing values.
              // This handles cases where hcl2json might fail on specific syntax or where we want to 
              // ensure consistency for variables like gh_org or business_unit.
              console.log('[GithubTerraformModuleEditor] Checking for missing values using regex scanner');
                 
              // State for simple block parser
              let currentKey = '';
              let blockDepth = 0;
              let blockContent = '';
              let inBlock = false;
                 
              const lines = rendered.split('\n');
                 
              for (const line of lines) {
                 const trimmed = line.trim();
                 if (!trimmed) continue;
                    
                 // If we are currently capturing a block
                 if (inBlock) {
                     blockContent += line + '\n';
                     // Count braces to find end of block
                     const openBraces = (line.match(/\{/g) || []).length;
                     const closeBraces = (line.match(/\}/g) || []).length;
                     blockDepth += (openBraces - closeBraces);
                        
                     if (blockDepth === 0) {
                         // Block finished
                         inBlock = false;
                            
                         // Let's try to extract key-values from the block content roughly
                         const blockValues: Record<string, any> = {};
                         const blockLines = blockContent.split('\n');
                         for (const bLine of blockLines) {
                             // Match key = value, supporting quoted keys
                             const bMatch = bLine.match(/^\s*((?:"[^"]+")|\w+)\s*=\s*(.+?)\s*$/);
                             if (bMatch) {
                                  let key = bMatch[1];
                                  if (key.startsWith('"') && key.endsWith('"')) key = key.slice(1, -1);
                                  
                                  let val = bMatch[2];
                                  if (val.startsWith('"') && val.endsWith('"')) val = val.slice(1, -1);
                                  else if (val.startsWith('var.')) val = `\${${val}}`;
                                  blockValues[key] = val;
                             }
                         }
                            
                         // Only set if not already present or if present is empty
                         if (parsedValues[currentKey] === undefined || 
                             (typeof parsedValues[currentKey] === 'object' && Object.keys(parsedValues[currentKey] as object).length === 0)) {
                             parsedValues[currentKey] = blockValues;
                         }
                         currentKey = '';
                         blockContent = '';
                     }
                     continue;
                 }

                 // Match top-level assignments: key = value OR key = {
                 const match = line.match(/^\s*((?:"[^"]+")|\w+)\s*=\s*(.+?)\s*$/);
                 if (match) {
                     let key = match[1];
                     if (key.startsWith('"') && key.endsWith('"')) key = key.slice(1, -1);
                     
                     let val = match[2];
                        
                     // Check for start of block
                     if (val.endsWith('{')) {
                         inBlock = true;
                         currentKey = key;
                         blockDepth = 1;
                         blockContent = ''; 
                         continue;
                     }

                     // Handle strings
                     if (val.startsWith('"') && val.endsWith('"')) {
                         val = val.slice(1, -1);
                     } 
                     // Handle bare variable references: var.something -> ${var.something}
                     else if (val.startsWith('var.')) {
                         val = `\${${val}}`;
                     }
                     // Handle booleans/numbers
                     else if (val === 'true') val = true as any;
                     else if (val === 'false') val = false as any;
                        
                     // Only set if not already present or if present is empty
                     if (parsedValues[key] === undefined || parsedValues[key] === "" || parsedValues[key] === null) {
                         parsedValues[key] = val;
                     }
                 }
              }

              // Apply extracted values to initialData
              console.log('[GithubTerraformModuleEditor] Applying extracted values:', parsedValues);
              for (const [key, value] of Object.entries(parsedValues)) {
                initialData[key] = value;
              }
              
              if (parsedValues.name && typeof parsedValues.name === 'string') {
                  // Ensure module name is also set if found via fallback
                   setModuleInfo(prev => ({ ...prev, name: parsedValues.name as string }));
              }
            } catch (renderErr) {
              console.error('[GithubTerraformModuleEditor] Template rendering error:', renderErr);
              setTemplateError(`Template rendering error: ${renderErr instanceof Error ? renderErr.message : String(renderErr)}`);
            }
          } catch (fetchErr) {
            console.error('[GithubTerraformModuleEditor] Failed to fetch template:', fetchErr);
            setWarnings(prev => [...prev, `Failed to fetch template file: ${fetchErr instanceof Error ? fetchErr.message : String(fetchErr)}`]);
          }
        }

        // Store original values (empty in template mode)
        if (!originalValuesRef.current) {
          originalValuesRef.current = {};
        }

        // Add module metadata
        initialData._moduleSource = configModuleSource;
        // Don't set _moduleVersion in template mode (use latest)

        // Add template configuration for the backend action to use
        if (templateFile) {
          initialData._templateFile = templateFile;
        }
        // Store the actual form values (not unresolved template expressions) for the backend action
        if (formValues) {
          // Extract only the values that correspond to template parameters
          const resolvedTemplateValues: Record<string, unknown> = {};
          if (templateValues) {
            // For each key in templateValues, get the actual value from formValues
            for (const key of Object.keys(templateValues as Record<string, unknown>)) {
              if (formValues[key] !== undefined) {
                resolvedTemplateValues[key] = formValues[key];
              }
            }
          }
          initialData._templateValues = resolvedTemplateValues;
        }
        if (outputFileName) {
          // Resolve template expressions in outputFileName using formValues (actual form data)
          const resolvedName = outputFileName.replace(
            /\$\{\{\s*(parameters|values)\.(\w+)\s*\}\}/g,
            (match, _namespace, key) => {
              const value = formValues?.[key];
              if (value === undefined || value === null) return match;
              return String(value);
            },
          );
          initialData._outputFileName = resolvedName;
        }
        if (outputPath) {
          initialData._outputPath = outputPath;
        }

        console.log('[GithubTerraformModuleEditor] Template mode initialData:', initialData);
        setLocalValues(initialData);
        // We don't call onChange(initialData) here because that would send an object.
        // The useEffect will pick up the new localValues and rawTemplateContent 
        // and call onChange(renderedString).
      } catch (err) {
        setError(extractErrorMessage(err));
      } finally {
        setLoading(false);
      }
      return;
    }

    // Normal mode: require repo and file
    if (!repoName || !filePath) {
      console.log('[GithubTerraformModuleEditor] Missing repoName or filePath, skipping');
      setVariables([]);
      setError(null);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      // Check if WASM is available in this browser
      const wasmAvailable = isWasmAvailable();
      console.log('[GithubTerraformModuleEditor] WASM available:', wasmAvailable);
      if (!wasmAvailable) {
        throw new Error('WebAssembly is not supported in this browser');
      }

      // Step 1: Fetch the file content from GitHub
      console.log('[GithubTerraformModuleEditor] Step 1: Getting GitHub token...');
      const token = await githubAuth.getAccessToken(['repo']);
      console.log('[GithubTerraformModuleEditor] Got GitHub token, fetching file...');

      const githubUrl = branch
        ? `https://api.github.com/repos/${owner}/${repoName}/contents/${filePath}?ref=${branch}`
        : `https://api.github.com/repos/${owner}/${repoName}/contents/${filePath}`;
      console.log('[GithubTerraformModuleEditor] GitHub URL:', githubUrl, branch ? `(branch: ${branch})` : '(default branch)');

      const fileResponse = await fetch(
        githubUrl,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            Accept: 'application/vnd.github.v3+json',
          },
        },
      );

      console.log('[GithubTerraformModuleEditor] GitHub response status:', fileResponse.status);

      if (!fileResponse.ok) {
        if (fileResponse.status === 404) {
          throw new Error(`File not found: ${filePath}`);
        }
        throw new Error(`GitHub API error: ${fileResponse.statusText}`);
      }

      const fileData = await fileResponse.json();
      const fileContent = atob(fileData.content);
      console.log('[GithubTerraformModuleEditor] File content length:', fileContent.length);
      console.log('[GithubTerraformModuleEditor] File content preview:', fileContent.substring(0, 200));

      // Step 2: Parse HCL in the browser using WASM
      console.log('[GithubTerraformModuleEditor] Step 2: Parsing HCL with WASM...');
      const parsed = await parseHcl(rawFileValue || 'module.tf', fileContent);
      console.log('[GithubTerraformModuleEditor] HCL parsed successfully:', {
        hasModule: !!parsed.module,
        hasVariable: !!parsed.variable,
        moduleKeys: parsed.module ? Object.keys(parsed.module) : [],
      });

      const moduleBlocks = extractModuleBlocks(parsed);
      console.log('[GithubTerraformModuleEditor] Extracted module blocks:', moduleBlocks.length);
      moduleBlocks.forEach((m, i) => {
        console.log(`[GithubTerraformModuleEditor] Module ${i}:`, {
          name: m.name,
          source: m.source,
          version: m.version,
          currentValuesKeys: Object.keys(m.currentValues),
        });
      });

      if (moduleBlocks.length === 0) {
        throw new Error('No module blocks found in the Terraform file');
      }

      // Step 3: Get the target module (by name or index)
      let targetModule;
      if (targetModuleName) {
        // Find module by name
        targetModule = moduleBlocks.find(m => m.name === targetModuleName);
        if (!targetModule) {
          console.warn(`[GithubTerraformModuleEditor] Module '${targetModuleName}' not found, available modules:`,
            moduleBlocks.map(m => m.name));
          targetModule = moduleBlocks[0];
        }
      } else {
        // Fall back to index-based selection
        targetModule = moduleBlocks[moduleIndex] || moduleBlocks[0];
      }
      console.log('[GithubTerraformModuleEditor] Step 3: Target module:', {
        name: targetModule.name,
        source: targetModule.source,
        version: targetModule.version,
        selectedBy: targetModuleName ? `name: ${targetModuleName}` : `index: ${moduleIndex}`,
      });

      const parsedSourceInfo = parseModuleSource(targetModule.source);
      console.log('[GithubTerraformModuleEditor] Parsed source info:', parsedSourceInfo);

      // Store source info for version changes
      setSourceInfo(parsedSourceInfo);

      // Fetch available versions in parallel with variables
      console.log('[GithubTerraformModuleEditor] Fetching available versions...');
      setVersionsLoading(true);
      fetchModuleVersions(parsedSourceInfo, {
        githubToken: token,
        fetchApi,
        backendUrl,
      }).then(versionsResult => {
        console.log('[GithubTerraformModuleEditor] Versions fetched:', versionsResult);
        setAvailableVersions(versionsResult.versions);
        setLatestVersion(versionsResult.latestVersion || null);
        // Initialize selected version from current module version or 'latest'
        if (targetModule.version) {
          setSelectedVersion(targetModule.version);
        } else {
          setSelectedVersion('latest');
        }
        setVersionsLoading(false);
      }).catch(err => {
        console.error('[GithubTerraformModuleEditor] Failed to fetch versions:', err);
        setVersionsLoading(false);
      });

      // Step 4: Fetch variables based on source type
      console.log('[GithubTerraformModuleEditor] Step 4: Fetching variables...', {
        sourceType: parsedSourceInfo.type,
        registryType: parsedSourceInfo.registryType,
        backendUrl,
      });

      const variablesResult: FetchVariablesResult = await fetchVariablesFromSource(
        parsedSourceInfo,
        {
          githubToken: token,
          fetchApi,
          backendUrl,
          version: targetModule.version,
          currentValues: targetModule.currentValues,
        },
      );

      console.log('[GithubTerraformModuleEditor] Variables fetched:', {
        count: variablesResult.variables.length,
        source: variablesResult.source,
        warnings: variablesResult.warnings,
        variableNames: variablesResult.variables.map(v => v.name),
      });

      // Build the response in the same format as the old backend
      const data: ParsedModuleResponse = {
        modules: moduleBlocks.map(m => ({
          name: m.name,
          source: m.source,
          version: m.version,
          currentValues: m.currentValues,
        })),
        variables: variablesResult.variables.map(v => ({
          ...v,
          currentValue: targetModule.currentValues[v.name],
        })),
        warnings: variablesResult.warnings,
        moduleSource: targetModule.source,
        moduleVersion: targetModule.version,
      };

      console.log('[GithubTerraformModuleEditor] Built response data:', {
        modulesCount: data.modules.length,
        variablesCount: data.variables.length,
        warnings: data.warnings,
      });

      setVariables(data.variables);
      setWarnings(data.warnings || []);

      const targetModuleInfo = {
        source: data.moduleSource,
        version: data.moduleVersion,
        name: data.modules[moduleIndex]?.name,
      };
      setModuleInfo(targetModuleInfo);

      // Initialize form data with current values or defaults
      // Parse existing form data to ensure any JSON strings are converted to proper objects
      const initialData: Record<string, unknown> = {};
      let hasChanges = false;

      // First, parse any existing formData values that might be JSON strings
      // This handles cases where wizard navigation serializes objects
      for (const [key, value] of Object.entries(formData)) {
        const parsed = parseJsonStrings(value);
        initialData[key] = parsed;
        if (parsed !== value) {
          hasChanges = true;
        }
      }

      // Apply current values from the module block
      const currentModule = data.modules[moduleIndex];
      if (currentModule?.currentValues) {
        for (const [key, value] of Object.entries(currentModule.currentValues)) {
          if (initialData[key] === undefined) {
            // Parse values from the backend in case they're JSON strings
            initialData[key] = parseJsonStrings(value);
            hasChanges = true;
          }
        }
      }

      // Then apply defaults for any missing values
      for (const variable of data.variables) {
        if (initialData[variable.name] === undefined) {
          if (variable.currentValue !== undefined) {
            initialData[variable.name] = parseJsonStrings(variable.currentValue);
            hasChanges = true;
          } else if (variable.default !== undefined) {
            initialData[variable.name] = parseJsonStrings(variable.default);
            hasChanges = true;
          }
        }
      }

      // Store original values for change tracking (only on first load)
      if (!originalValuesRef.current) {
        // Deep clone the initial data as original values
        originalValuesRef.current = JSON.parse(JSON.stringify(initialData));
      }

      // Add module metadata for the backend action (avoids WASM parsing on backend)
      // These are prefixed with underscore to avoid conflicts with module variable names
      // Only mark as changed if values are actually different to prevent re-render loops
      if (targetModuleInfo.name && initialData._moduleName !== targetModuleInfo.name) {
        initialData._moduleName = targetModuleInfo.name;
        hasChanges = true;
      }
      if (targetModuleInfo.source && initialData._moduleSource !== targetModuleInfo.source) {
        initialData._moduleSource = targetModuleInfo.source;
        hasChanges = true;
      }
      if (targetModuleInfo.version && initialData._moduleVersion !== targetModuleInfo.version) {
        initialData._moduleVersion = targetModuleInfo.version;
        hasChanges = true;
      }

      // Update form data if there are changes (new values or parsed JSON strings)
      if (hasChanges) {
        onChange(initialData);
      }
    } catch (err) {
      setError(extractErrorMessage(err));
    } finally {
      setLoading(false);
    }
  }, [
    repoName,
    filePath,
    rawFileValue,
    owner,
    moduleIndex,
    backendUrl,
    githubAuth,
    fetchApi,
    formData,
    formValues,
    onChange,
    parseJsonStrings,
    templateMode,
    configModuleSource,
    templateFile,
    templateValues,
    outputFileName,
    outputPath,
    templateRepo,
    templateRepoPath,
    renderTemplate,
  ]);

  useEffect(() => {
    console.log('[GithubTerraformModuleEditor] useEffect triggered', { repoName, filePath, branch, targetModuleName, templateMode, configModuleSource });
    fetchModuleVariables();
  }, [repoName, filePath, branch, targetModuleName, templateMode, configModuleSource]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleVariableChange = useCallback(
    (variableName: string, value: unknown) => {
      // Track that this variable has been edited - keep it visible even when "show hidden" is off
      setEditedVariables(prev => {
        const newSet = new Set(prev);
        newSet.add(variableName);
        return newSet;
      });

      // Parse JSON strings to ensure proper typing before passing to scaffolder
      const parsedValue = parseJsonStrings(value);

      // Update local state
      const newValues = { ...localValues, [variableName]: parsedValue };
      setLocalValues(newValues);

      // In non-template mode, we need to manually trigger onChange with the object.
      // In template mode, the useEffect will trigger onChange with the rendered string.
      if (!templateMode) {
        onChange({
          ...formData, // Keep existing metadata and other fields
          [variableName]: parsedValue,
        });
      }
    },
    [formData, onChange, parseJsonStrings, localValues, templateMode],
  );

  // Handle version selection change
  const handleVersionChange = useCallback(
    async (newVersion: string | 'latest') => {
      console.log('[GithubTerraformModuleEditor] Version changed to:', newVersion);
      setSelectedVersion(newVersion);

      if (!sourceInfo) {
        console.warn('[GithubTerraformModuleEditor] No sourceInfo available for version change');
        return;
      }

      setLoading(true);
      try {
        // Get the actual version to fetch (use latest version if 'latest' selected)
        const versionToFetch = newVersion === 'latest' ? latestVersion : newVersion;

        // Get GitHub token for GitHub sources
        const token = await githubAuth.getAccessToken(['repo']);

        // Fetch variables for the new version
        const variablesResult = await fetchVariablesFromSource(sourceInfo, {
          githubToken: token,
          fetchApi,
          backendUrl,
          version: versionToFetch || undefined,
          currentValues: {},
        });

        console.log('[GithubTerraformModuleEditor] New version variables:', {
          count: variablesResult.variables.length,
          variableNames: variablesResult.variables.map(v => v.name),
        });

        // Find incompatible variables (exist in formData but not in new version's variables)
        const newVariableNames = new Set(variablesResult.variables.map(v => v.name));
        const incompatible = new Set<string>();

        // Check each current localValues key against new variables
        // Skip metadata keys that start with underscore
        for (const key of Object.keys(localValues)) {
          if (!key.startsWith('_') && !newVariableNames.has(key) && localValues[key] !== undefined) {
            incompatible.add(key);
          }
        }

        console.log('[GithubTerraformModuleEditor] Incompatible variables:', Array.from(incompatible));
        setIncompatibleVariables(incompatible);

        // Update variables with current values applied
        const updatedVariables = variablesResult.variables.map(v => ({
          ...v,
          currentValue: localValues[v.name],
        }));

        setVariables(updatedVariables);
        setWarnings(variablesResult.warnings);

        // Update module version in localValues
        // If 'latest' is selected, set _moduleVersion to undefined (will be omitted from output)
        const newLocalValues = {
          ...localValues,
          _moduleVersion: newVersion === 'latest' ? undefined : newVersion,
        };
        setLocalValues(newLocalValues);

        // Update the moduleInfo state
        setModuleInfo(prev => ({
          ...prev,
          version: newVersion === 'latest' ? undefined : newVersion,
        }));

        if (!templateMode) {
            onChange({
                ...formData,
                ...newLocalValues
            });
        }
      } catch (err) {
        console.error('[GithubTerraformModuleEditor] Error changing version:', err);
        setWarnings(prev => [...prev, `Failed to fetch variables for version ${newVersion}`]);
      } finally {
        setLoading(false);
      }
    },
    [sourceInfo, latestVersion, githubAuth, fetchApi, backendUrl, formData, onChange],
  );

  // Helper to check if a variable's value is hidden
  const isVariableHidden = (v: TerraformVariableSchema) => {
    const value = localValues[v.name];
    return isHiddenValue(value, v);
  };

  // Group variables by required/optional, then filter hidden if needed
  const allRequiredVars = variables.filter(v => v.required && !(hideSensitive && v.sensitive));
  const allOptionalVars = variables.filter(v => !v.required && !(hideSensitive && v.sensitive));
  const sensitiveVars = hideSensitive
    ? []
    : variables.filter(v => v.sensitive);

  // Count hidden variables
  const hiddenRequiredCount = allRequiredVars.filter(isVariableHidden).length;
  const hiddenOptionalCount = allOptionalVars.filter(isVariableHidden).length;
  const totalHiddenCount = hiddenRequiredCount + hiddenOptionalCount;

  // Filter to visible variables unless showHiddenVariables is true
  // Also include variables that have been edited (so they stay visible while being worked on)
  const requiredVars = showHiddenVariables
    ? allRequiredVars
    : allRequiredVars.filter(v => !isVariableHidden(v) || editedVariables.has(v.name));
  const optionalVars = showHiddenVariables
    ? allOptionalVars
    : allOptionalVars.filter(v => !isVariableHidden(v) || editedVariables.has(v.name));

  // In normal mode, require repo and file. In template mode, require moduleSource.
  if (!templateMode && (!repoName || !filePath)) {
    console.log('[GithubTerraformModuleEditor] Early return: missing repoName or filePath');
    return (
      <Box className={classes.root}>
        <Alert severity="info">
          Please select a repository and file to load module variables.
        </Alert>
      </Box>
    );
  }

  if (templateMode && !configModuleSource) {
    console.log('[GithubTerraformModuleEditor] Early return: template mode but no moduleSource');
    return (
      <Box className={classes.root}>
        <Alert severity="error">
          Template mode requires moduleSource to be configured in ui:options.
        </Alert>
      </Box>
    );
  }

  if (loading) {
    console.log('[GithubTerraformModuleEditor] Early return: loading');
    const loadingMessage = templateMode
      ? `Loading module variables from ${configModuleSource}...`
      : `Loading module variables from ${repoName}/${filePath}...`;
    return (
      <Box className={classes.loadingContainer}>
        <CircularProgress size={24} />
        <Typography variant="body2" style={{ marginLeft: 16 }}>
          {loadingMessage}
        </Typography>
      </Box>
    );
  }

  if (error) {
    console.log('[GithubTerraformModuleEditor] Early return: error', error);
    return (
      <Box className={classes.root}>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  if (variables.length === 0) {
    console.log('[GithubTerraformModuleEditor] Early return: no variables');
    return (
      <Box className={classes.root}>
        <Alert severity="info">
          No module variables found in the selected file.
        </Alert>
      </Box>
    );
  }

  return (
    <EditorProvider>
      <Box className={classes.root}>
        <Box className={classes.header}>
          <Typography variant="h6">
            {schema.title || 'Module Variables'}
            {required && <span style={{ color: 'red' }}> *</span>}
          </Typography>
          <Typography variant="caption" color="textSecondary">
            {variables.length} variable{variables.length !== 1 ? 's' : ''}
          </Typography>
        </Box>

        {/* Mode Toggle */}
        <Box className={classes.modeToggle}>
          <Typography variant="body2" style={{ marginRight: 12 }}>
            Edit Mode:
          </Typography>
          <ToggleButtonGroup
            value={editorMode}
            exclusive
            onChange={handleModeChange}
            size="small"
          >
            <ToggleButton value="gui">
              GUI
            </ToggleButton>
            <ToggleButton value="text">
              Text
            </ToggleButton>
            <ToggleButton value="raw">
              Raw JSON
            </ToggleButton>
          </ToggleButtonGroup>
        </Box>

        {schema.description && showDescriptions && (
          <Typography
            variant="body2"
            color="textSecondary"
            paragraph
            style={{ marginBottom: 16 }}
          >
            {schema.description}
          </Typography>
        )}

        {/* Module info */}
        <Box className={classes.moduleInfo}>
          {moduleInfo.name && (
            <Chip
              size="small"
              label={`Module: ${moduleInfo.name}`}
              className={classes.moduleChip}
              color="primary"
            />
          )}
          {moduleInfo.source && (
            <Chip
              size="small"
              label={`Source: ${moduleInfo.source}`}
              className={classes.moduleChip}
              variant="outlined"
            />
          )}
        </Box>

        {/* Version selector */}
        {availableVersions.length > 0 && (
          <Box className={classes.versionSelector}>
            <FormControl variant="outlined" size="small" className={classes.versionSelect}>
              <InputLabel id="version-select-label">Module Version</InputLabel>
              <Select
                labelId="version-select-label"
                value={selectedVersion || ''}
                onChange={(e) => handleVersionChange(e.target.value as string)}
                label="Module Version"
                disabled={versionsLoading || loading}
              >
                <MenuItem value="latest">
                  <Box display="flex" alignItems="center">
                    Latest
                    {latestVersion && (
                      <Chip
                        size="small"
                        label={latestVersion}
                        className={classes.latestChip}
                        style={{ marginLeft: 8 }}
                      />
                    )}
                  </Box>
                </MenuItem>
                <Divider />
                {availableVersions.map((v) => (
                  <MenuItem key={v.version} value={v.version}>
                    {v.version}
                    {v.version === latestVersion && (
                      <Chip
                        size="small"
                        label="latest"
                        className={classes.latestChip}
                        style={{ marginLeft: 8 }}
                      />
                    )}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {/* Module links */}
            <Box className={classes.moduleLinks}>
              {/* Terraform Registry link */}
              {getTerraformRegistryUrl(sourceInfo, selectedVersion) && (
                <Tooltip title="View in Terraform Registry">
                  <IconButton
                    size="small"
                    className={classes.moduleLink}
                    href={getTerraformRegistryUrl(sourceInfo, selectedVersion) || ''}
                    target="_blank"
                    rel="noopener noreferrer"
                    component="a"
                  >
                    <svg
                      className={classes.terraformIcon}
                      viewBox="0 0 128 128"
                      fill="currentColor"
                    >
                      <path d="M77.941 44.5v36.836L46.324 62.918V26.082zm0 42.836L46.324 106V69.164l31.617-18.418zM81.41 81.336l31.617-18.418V26.082L81.41 44.5zM11.613 42.918L43.23 24.5v36.836L11.613 79.754z" />
                    </svg>
                  </IconButton>
                </Tooltip>
              )}

              {/* GitHub Repository link */}
              {getGitHubUrl(sourceInfo) && (
                <Tooltip title="View on GitHub">
                  <IconButton
                    size="small"
                    className={classes.moduleLink}
                    href={getGitHubUrl(sourceInfo) || ''}
                    target="_blank"
                    rel="noopener noreferrer"
                    component="a"
                  >
                    <GitHubIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              )}

              {/* Backstage Catalog link */}
              {backstageEntityLink && (
                <Tooltip title="View in Backstage Catalog">
                  <IconButton
                    size="small"
                    className={classes.moduleLink}
                    href={backstageEntityLink}
                    component="a"
                  >
                    <DescriptionIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              )}
            </Box>

            {versionsLoading && <CircularProgress size={20} />}
            {selectedVersion === 'latest' && (
              <Typography variant="caption" color="textSecondary">
                Using latest version will remove the version constraint from the module
              </Typography>
            )}
          </Box>
        )}

        {/* Incompatible variables warning */}
        {incompatibleVariables.size > 0 && (
          <Alert severity="error" className={classes.warningAlert}>
            <Typography variant="body2">
              <strong>Incompatible variables detected!</strong>
            </Typography>
            <Typography variant="body2">
              The following variables exist in your configuration but are not available in version {selectedVersion}:
            </Typography>
            <ul style={{ margin: '8px 0', paddingLeft: 20 }}>
              {Array.from(incompatibleVariables).map((varName) => (
                <li key={varName}>
                  <code>{varName}</code>: {JSON.stringify(localValues[varName])}
                </li>
              ))}
            </ul>
            <Typography variant="body2" color="textSecondary">
              These values will be preserved but may cause Terraform errors. Consider removing them or choosing a compatible version.
            </Typography>
          </Alert>
        )}

        {/* Warnings */}
        {warnings.length > 0 && (
          <Alert severity="warning" className={classes.warningAlert}>
            <Typography variant="body2">Parsing warnings:</Typography>
            <ul style={{ margin: '8px 0', paddingLeft: 20 }}>
              {warnings.map((warning, i) => (
                <li key={i}>{warning}</li>
              ))}
            </ul>
          </Alert>
        )}

        {/* Template error */}
        {templateError && (
          <Alert severity="error" className={classes.warningAlert}>
            <Typography variant="body2">{templateError}</Typography>
          </Alert>
        )}

        {/* Template preview (in template mode) */}
        {templateMode && renderedTemplateContent && (
          <Accordion defaultExpanded={false} style={{ marginBottom: 16 }}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography variant="subtitle2">
                Preview: {resolvedOutputFileName}
              </Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Box
                component="pre"
                style={{
                  width: '100%',
                  maxHeight: 400,
                  overflow: 'auto',
                  backgroundColor: '#f5f5f5',
                  padding: 16,
                  borderRadius: 4,
                  fontSize: 12,
                  fontFamily: 'monospace',
                  margin: 0,
                  whiteSpace: 'pre-wrap',
                  wordBreak: 'break-word',
                }}
              >
                {renderedTemplateContent}
              </Box>
            </AccordionDetails>
          </Accordion>
        )}

        {/* Show/Hide hidden variables button (for GUI and Text modes) */}
        {totalHiddenCount > 0 && editorMode !== 'raw' && (
          <Button
            size="small"
            variant="text"
            startIcon={showHiddenVariables ? <VisibilityOffIcon /> : <VisibilityIcon />}
            onClick={() => {
              const newShowHidden = !showHiddenVariables;
              setShowHiddenVariables(newShowHidden);
              // When hiding, also clear the edited variables tracking
              // so they follow the normal hidden rules again
              if (!newShowHidden) {
                setEditedVariables(new Set());
              }
            }}
            className={classes.showHiddenButton}
          >
            {showHiddenVariables
              ? 'Hide null/derived values'
              : `Show ${totalHiddenCount} hidden value${totalHiddenCount !== 1 ? 's' : ''}`}
          </Button>
        )}

        {/* Raw JSON Mode Editor */}
        {editorMode === 'raw' && (
          <Box>
            <textarea
              className={`${classes.textEditor} ${rawEditorError ? classes.textEditorError : ''}`}
              value={rawEditorValue}
              onChange={handleRawEditorChange}
              spellCheck={false}
            />
            {rawEditorError && (
              <Typography variant="caption" color="error" style={{ marginTop: 4, display: 'block' }}>
                {rawEditorError}
              </Typography>
            )}
          </Box>
        )}

        {/* Text Mode Editor - Simple text fields per variable */}
        {editorMode === 'text' && (
          <>
            {/* Required Variables */}
            {requiredVars.length > 0 && (
              <Box className={classes.section}>
                <Box className={classes.sectionTitle}>
                  <Typography variant="subtitle1">Required Variables</Typography>
                  <Chip
                    size="small"
                    label={requiredVars.length}
                    color="secondary"
                    className={classes.badge}
                  />
                </Box>
                {requiredVars.map(variable => (
                  <Paper key={variable.name} className={classes.variableCard} variant="outlined">
                    <TextField
                      fullWidth
                      size="small"
                      variant="outlined"
                      label={
                        <>
                          {variable.name}
                          {variable.required && <span style={{ color: 'red' }}> *</span>}
                        </>
                      }
                      multiline
                      minRows={variable.type === 'object' || variable.type === 'list' || variable.type === 'map' ? 3 : 1}
                      value={
                        typeof localValues[variable.name] === 'string'
                          ? localValues[variable.name]
                          : JSON.stringify(localValues[variable.name], null, 2) ?? ''
                      }
                      onChange={e => {
                        const val = e.target.value;
                        try {
                          handleVariableChange(variable.name, JSON.parse(val));
                        } catch {
                          handleVariableChange(variable.name, val);
                        }
                      }}
                      helperText={variable.description}
                      InputProps={{
                        style: { fontFamily: 'monospace', fontSize: 12 },
                      }}
                    />
                  </Paper>
                ))}
              </Box>
            )}

            {/* Optional Variables */}
            {optionalVars.length > 0 && (
              <Box className={classes.section}>
                <Box className={classes.sectionTitle}>
                  <Typography variant="subtitle1">Optional Variables</Typography>
                  <Chip
                    size="small"
                    label={optionalVars.length}
                    className={classes.badge}
                  />
                </Box>
                {optionalVars.map(variable => (
                  <Paper key={variable.name} className={classes.variableCard} variant="outlined">
                    <TextField
                      fullWidth
                      size="small"
                      variant="outlined"
                      label={variable.name}
                      multiline
                      minRows={variable.type === 'object' || variable.type === 'list' || variable.type === 'map' ? 3 : 1}
                      value={
                        typeof localValues[variable.name] === 'string'
                          ? localValues[variable.name]
                          : JSON.stringify(localValues[variable.name], null, 2) ?? ''
                      }
                      onChange={e => {
                        const val = e.target.value;
                        try {
                          handleVariableChange(variable.name, JSON.parse(val));
                        } catch {
                          handleVariableChange(variable.name, val);
                        }
                      }}
                      helperText={variable.description}
                      InputProps={{
                        style: { fontFamily: 'monospace', fontSize: 12 },
                      }}
                    />
                  </Paper>
                ))}
              </Box>
            )}

            {/* Sensitive Variables */}
            {sensitiveVars.length > 0 && (
              <Box className={classes.section}>
                <Divider style={{ marginBottom: 16 }} />
                <Box className={classes.sectionTitle}>
                  <Typography variant="subtitle1">Sensitive Variables</Typography>
                  <Chip
                    size="small"
                    label={sensitiveVars.length}
                    color="default"
                    className={classes.badge}
                  />
                </Box>
                <Alert severity="warning" style={{ marginBottom: 16 }}>
                  These variables are marked as sensitive. Values will be masked.
                </Alert>
                {sensitiveVars.map(variable => (
                  <Paper key={variable.name} className={classes.variableCard} variant="outlined">
                    <TextField
                      fullWidth
                      size="small"
                      variant="outlined"
                      label={variable.name}
                      type="password"
                      value={
                        typeof localValues[variable.name] === 'string'
                          ? localValues[variable.name]
                          : JSON.stringify(localValues[variable.name], null, 2) ?? ''
                      }
                      onChange={e => {
                        const val = e.target.value;
                        try {
                          handleVariableChange(variable.name, JSON.parse(val));
                        } catch {
                          handleVariableChange(variable.name, val);
                        }
                      }}
                      helperText={variable.description}
                    />
                  </Paper>
                ))}
              </Box>
            )}
          </>
        )}

        {/* GUI Mode Editor - Structured type-aware editors */}
        {editorMode === 'gui' && (
          <>
            {/* Required Variables */}
            {requiredVars.length > 0 && (
              <Box className={classes.section}>
                <Box className={classes.sectionTitle}>
                  <Typography variant="subtitle1">Required Variables</Typography>
                  <Chip
                    size="small"
                    label={requiredVars.length}
                    color="secondary"
                    className={classes.badge}
                  />
                </Box>
                {requiredVars.map(variable => (
                  <Paper key={variable.name} className={classes.variableCard} variant="outlined">
                    <Box className={classes.variableHeader}>
                      <Box className={classes.variableContent}>
                        <VariableEditor
                          variable={variable}
                          value={localValues[variable.name]}
                          onChange={value => handleVariableChange(variable.name, value)}
                        />
                      </Box>
                      {variable.rawType && (
                        <Tooltip title="View type definition">
                          <IconButton
                            size="small"
                            className={classes.helpIcon}
                            onClick={() => setTypeDefinitionDialog({
                              open: true,
                              variableName: variable.name,
                              rawType: variable.rawType || '',
                            })}
                          >
                            <HelpOutlineIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                    </Box>
                  </Paper>
                ))}
              </Box>
            )}

            {/* Optional Variables */}
            {optionalVars.length > 0 && (
              <Box className={classes.section}>
                <Box className={classes.sectionTitle}>
                  <Typography variant="subtitle1">Optional Variables</Typography>
                  <Chip
                    size="small"
                    label={optionalVars.length}
                    className={classes.badge}
                  />
                </Box>
                {optionalVars.map(variable => (
                  <Paper key={variable.name} className={classes.variableCard} variant="outlined">
                    <Box className={classes.variableHeader}>
                      <Box className={classes.variableContent}>
                        <VariableEditor
                          variable={variable}
                          value={localValues[variable.name]}
                          onChange={value => handleVariableChange(variable.name, value)}
                        />
                      </Box>
                      {variable.rawType && (
                        <Tooltip title="View type definition">
                          <IconButton
                            size="small"
                            className={classes.helpIcon}
                            onClick={() => setTypeDefinitionDialog({
                              open: true,
                              variableName: variable.name,
                              rawType: variable.rawType || '',
                            })}
                          >
                            <HelpOutlineIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                    </Box>
                  </Paper>
                ))}
              </Box>
            )}

            {/* Sensitive Variables */}
            {sensitiveVars.length > 0 && (
              <Box className={classes.section}>
                <Divider style={{ marginBottom: 16 }} />
                <Box className={classes.sectionTitle}>
                  <Typography variant="subtitle1">Sensitive Variables</Typography>
                  <Chip
                    size="small"
                    label={sensitiveVars.length}
                    color="default"
                    className={classes.badge}
                  />
                </Box>
                <Alert severity="warning" style={{ marginBottom: 16 }}>
                  These variables are marked as sensitive. Values will be masked.
                </Alert>
                {sensitiveVars.map(variable => (
                  <Paper key={variable.name} className={classes.variableCard} variant="outlined">
                    <Box className={classes.variableHeader}>
                      <Box className={classes.variableContent}>
                        <VariableEditor
                          variable={variable}
                          value={localValues[variable.name]}
                          onChange={value => handleVariableChange(variable.name, value)}
                        />
                      </Box>
                      {variable.rawType && (
                        <Tooltip title="View type definition">
                          <IconButton
                            size="small"
                            className={classes.helpIcon}
                            onClick={() => setTypeDefinitionDialog({
                              open: true,
                              variableName: variable.name,
                              rawType: variable.rawType || '',
                            })}
                          >
                            <HelpOutlineIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                    </Box>
                  </Paper>
                ))}
              </Box>
            )}

            {/* Incompatible Variables - shown in red */}
            {incompatibleVariables.size > 0 && (
              <Box className={classes.section}>
                <Divider style={{ marginBottom: 16 }} />
                <Box className={classes.sectionTitle}>
                  <WarningIcon color="error" />
                  <Typography variant="subtitle1" color="error">
                    Incompatible Variables
                  </Typography>
                  <Chip
                    size="small"
                    label={incompatibleVariables.size}
                    color="secondary"
                    className={classes.badge}
                  />
                </Box>
                {Array.from(incompatibleVariables).map(varName => (
                  <Paper
                    key={varName}
                    className={`${classes.variableCard} ${classes.incompatibleCard}`}
                    variant="outlined"
                  >
                    <Box className={classes.incompatibleWarning}>
                      <WarningIcon fontSize="small" />
                      <Typography variant="caption">
                        This variable is not available in version {selectedVersion}
                      </Typography>
                    </Box>
                    <TextField
                      fullWidth
                      size="small"
                      variant="outlined"
                      label={varName}
                      multiline
                      minRows={typeof localValues[varName] === 'object' ? 3 : 1}
                      value={
                        typeof localValues[varName] === 'string'
                          ? localValues[varName]
                          : JSON.stringify(localValues[varName], null, 2) ?? ''
                      }
                      onChange={e => {
                        const val = e.target.value;
                        try {
                          handleVariableChange(varName, JSON.parse(val));
                        } catch {
                          handleVariableChange(varName, val);
                        }
                      }}
                      error
                      helperText="This value may cause Terraform errors. Consider removing it."
                      InputProps={{
                        style: { fontFamily: 'monospace', fontSize: 12 },
                      }}
                    />
                    <Button
                      size="small"
                      color="secondary"
                      style={{ marginTop: 8 }}
                      onClick={() => {
                        // Remove this variable from localValues
                        const newValues = { ...localValues };
                        delete newValues[varName];
                        setLocalValues(newValues);
                        
                        if (!templateMode) {
                          // Remove from formData as well for non-template mode
                          const newData = { ...formData };
                          delete newData[varName];
                          onChange(newData);
                        }
                        
                        // Remove from incompatible set
                        setIncompatibleVariables(prev => {
                          const newSet = new Set(prev);
                          newSet.delete(varName);
                          return newSet;
                        });
                      }}
                    >
                      Remove Variable
                    </Button>
                  </Paper>
                ))}
              </Box>
            )}
          </>
        )}

        {/* Validation errors from scaffolder */}
        {rawErrors && rawErrors.length > 0 && (
          <Alert severity="error" style={{ marginTop: 16 }}>
            {rawErrors.map(err => (typeof err === 'string' ? err : String(err))).join(', ')}
          </Alert>
        )}

        {/* Type Definition Dialog */}
        <Dialog
          open={typeDefinitionDialog.open}
          onClose={() => setTypeDefinitionDialog({ open: false, variableName: '', rawType: '' })}
          maxWidth="md"
          fullWidth
        >
          <DialogTitle>
            Type Definition: {typeDefinitionDialog.variableName}
          </DialogTitle>
          <DialogContent>
            <pre className={classes.typeDefinitionCode}>
              {typeDefinitionDialog.rawType}
            </pre>
          </DialogContent>
        </Dialog>
      </Box>
    </EditorProvider>
  );
};

export default GithubTerraformModuleEditor;
